import { createResponse } from "./cloudFunctions"
import getNextBestQuestions from "./schemas/get_next_best_questions.json"

const suggestNextBestQuestions = async ({
  viewSet,
  views,
  setMessage,
  questionsTabFiles,
}) => {
  setMessage?.("Identifying next best questions to answer")

  // Answered question IDs (from existing views)
  const answeredQuestionIds = views.map((v) => v.question_id).filter(Boolean)

  // Already suggested question IDs
  const existingSuggestedViewQuestionIds = (viewSet.view_suggestions || []).map(
    (s) => s.question_id
  )

  const answeredQuestions = views.filter((v) =>
    answeredQuestionIds.includes(v.question_id)
  )

  // Not answered AND not already suggested
  const unansweredQuestions = (viewSet.view_questions || [])
    .filter((q) => !answeredQuestionIds.includes(q.question_id))
    .filter((q) => !existingSuggestedViewQuestionIds.includes(q.question_id))

  console.log("%cparams", "color:yellow", {
    answeredQuestionIds,
    existingSuggestedViewQuestionIds,
    answeredQuestions,
    unansweredQuestions,
    viewSet,
  })

  // Candidates (IDs only) we’ll allow the model to choose from
  const candidateIds = unansweredQuestions
    .map((q) => q.question_id)
    .filter(Boolean)

  // If there are no candidates, don't send an impossible schema — just return []
  if (candidateIds.length === 0) {
    console.warn(
      "No unanswered candidate questions available; returning empty list."
    )
    return []
  }

  // Target count (tweak as you like)
  const target = Math.min(3, Math.max(1, candidateIds.length))

  // ---- Build prompt (rules-first to prevent clarifications/meta) ----
  const lines = [
    "SYSTEM RULES (follow exactly):",
    `- Return EXACTLY ${target} items in next_best_questions.`,
    "- Select ONLY from the candidate question_ids provided below. NEVER invent an ID.",
    "- Do NOT ask questions, request confirmation, or add commentary.",
    "- Output MUST be a single JSON object that validates against the provided schema. No extra text.",
    "",
    `Objective: ${viewSet.purpose}`,
    "",
    answeredQuestions.length > 0
      ? "Already answered questions:"
      : "No questions have been answered yet.",
    ...answeredQuestions.map((q) => `ID [${q.question_id}]: ${q.question}`),
    "",
    "Already suggested (do NOT suggest again):",
    ...(viewSet.view_suggestions || []).map(
      (s) => `ID [${s.question_id}]: ${s.question}`
    ),
    "",
    "Candidates (choose from these IDs ONLY):",
    ...unansweredQuestions.map((q) => `ID [${q.question_id}]: ${q.question}`),
  ]

  // If we’re providing a file corpus, tell the model to use it.
  if (Array.isArray(questionsTabFiles) && questionsTabFiles.length > 0) {
    lines.push(
      "",
      "Consult the file search corpus and take into account any directly relevant information. Cite briefly where appropriate."
    )
  }

  const prompt = lines.join("\n")

  // ---- Tools: file_search over existing vector stores ----
  const tools =
    Array.isArray(questionsTabFiles) && questionsTabFiles.length > 0
      ? [
          {
            type: "file_search",
            vector_store_ids: questionsTabFiles.map((item) => item.id),
          },
        ]
      : undefined

  // ---- Build a runtime-constrained schema: exact count + ID enum ----
  const base = getNextBestQuestions.parameters
  const selectionSchema = JSON.parse(JSON.stringify(base)) // clone

  // Put min/max on the ARRAY (not inside items)
  if (
    !selectionSchema?.properties?.next_best_questions ||
    selectionSchema.properties.next_best_questions.type !== "array"
  ) {
    console.error(
      "get_next_best_questions.json is missing an array at properties.next_best_questions"
    )
    // Fall back to returning empty
    return []
  }

  selectionSchema.properties.next_best_questions.minItems = target
  selectionSchema.properties.next_best_questions.maxItems = target

  // Constrain items.question_id to candidate IDs; ensure required fields exist
  const item = selectionSchema.properties.next_best_questions.items || {}
  item.type = "object"
  item.additionalProperties = false
  item.properties = item.properties || {}
  item.properties.question_id = {
    type: "string",
    enum: candidateIds,
    description: "Choose ONLY from the provided candidate question_ids.",
  }
  item.properties.reason = {
    type: "string",
    minLength: 10,
    description:
      "Why answering this question helps reach the objective; mention ties to already-answered items if useful.",
  }
  item.required = ["question_id", "reason"]
  selectionSchema.properties.next_best_questions.items = item

  // ---- Call Responses via your callable helper ----
  const result = await createResponse({
    prompt,
    model: "gpt-5-mini", // mini is more reliable than nano for strict selection
    reasoning: { effort: "low" }, // keeps it less "chatty"
    tools,
    jsonSchema: getNextBestQuestions, // your helper maps to text.format
    store: true,
  })

  console.log("%cresponses result", "color:lightgreen", { result })

  // ---- Parse strict JSON output ----
  let nextBestQuestions = []
  try {
    const outputText =
      result?.response?.output_text ?? result?.output_text ?? ""
    const parsed = JSON.parse(outputText)
    nextBestQuestions = parsed.next_best_questions || []
  } catch (e) {
    console.warn("Failed to parse next best questions JSON", e)
  }

  // Final guard: ensure distinct IDs and only allowed candidates
  const allowed = new Set(candidateIds)
  const seen = new Set()
  nextBestQuestions = nextBestQuestions.filter((q) => {
    const id = q?.question_id
    if (!id || !allowed.has(id) || seen.has(id)) return false
    seen.add(id)
    return true
  })

  return nextBestQuestions
}

export { suggestNextBestQuestions }
